import { ChevronDown, Users } from "lucide-react";
import { useMemo, useState } from "react";
import { PersonAvatar } from "@/components/persons/PersonAvatar";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { FilterRow } from "@/components/shared/filter-bar";
import { FilterPanel, selectedLabels } from "@/components/shared/filter-panel";
import { FilterToggleGroup } from "@/components/shared/filter-toggle-group";
import { OriginLink } from "@/components/shared/origin-link";
import { SearchInput } from "@/components/shared/search-input";
import { Button } from "@/components/ui/button";
import { EntityComboboxMulti } from "@/components/ui/entity-combobox";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useEmploymentTypeOptions } from "@/hooks/use-employment-types";
import { useEntityOptionLabels } from "@/hooks/use-entity-options";
import type { Department, PersonLite, Pillar, RoleTeam } from "@/hooks/use-organization";
import { useOrganization } from "@/hooks/use-organization";
import { cn } from "@/lib/utils";

/**
 * The Board view of the organization: the same pillar → department → role-team
 * placement as the tree, laid out as a dense, collapsible, filterable roster.
 * Each person is a compact row accented by employment type (the FTE / contractor
 * / part-time signal from the "Resource Board" mockup) — a flatter, spreadsheet-
 * like read of who sits where, versus the tree's avatar-stack summary.
 */

// Left-accent bar + label per employment type, applied to each person row.
interface EmploymentAccent {
  bar: string;
  label: string;
  chip: string;
}

// Full-time is the unmarked default (no accent, muted label) so contractors and
// part-timers stand out — the point of the board is to see non-FTE placement at
// a glance.
const FTE_ACCENT: EmploymentAccent = {
  bar: "border-l-transparent",
  label: "FTE",
  chip: "text-muted-foreground",
};

/**
 * Card accents per employment type. Only the COLOUR and the short card label
 * live here — the filter chips read their labels from the admin-managed list
 * (EPIC-028). A type with no entry falls back to the full-time accent.
 */
/*
  Categorical, like the pillar hues in OrganizationTree — one per employment
  type, and amber here means "contractor", not "warning". Kept off the status
  tokens so the two colour languages stay separate.
*/
const EMPLOYMENT_ACCENT: Record<string, EmploymentAccent> = {
  full_time: FTE_ACCENT,
  part_time: {
    bar: "border-l-sky-400 dark:border-l-sky-500",
    label: "Part-time",
    chip: "text-sky-600 dark:text-sky-400",
  },
  contractor: {
    bar: "border-l-amber-400 dark:border-l-amber-500",
    label: "Contractor",
    chip: "text-amber-600 dark:text-amber-500",
  },
  variable: {
    bar: "border-l-violet-400 dark:border-l-violet-500",
    label: "Variable",
    chip: "text-violet-600 dark:text-violet-400",
  },
};

function accentFor(employmentType: string): EmploymentAccent {
  return EMPLOYMENT_ACCENT[employmentType] ?? FTE_ACCENT;
}

// Stable keys for the loading skeleton (no array-index keys per lint rules).
const SKELETON_KEYS = ["s1", "s2", "s3", "s4", "s5", "s6", "s7", "s8"];

interface BoardFilters {
  departmentIds: string[];
  roleTeamIds: string[];
  employmentTypes: string[];
  search: string;
}

const EMPTY_FILTERS: BoardFilters = {
  departmentIds: [],
  roleTeamIds: [],
  employmentTypes: [],
  search: "",
};

// ── Filtered view model ──────────────────────────────────────────────────

/**
 * Apply the board filters to the full org tree, dropping empty branches so the
 * board only renders pillars/departments/teams that still have matching people.
 * Pure — recomputed via useMemo when the tree or filters change.
 */
function filterPillars(pillars: Pillar[], filters: BoardFilters, search: string): Pillar[] {
  const term = search.trim().toLowerCase();
  const matchesPerson = (p: PersonLite) => {
    if (filters.employmentTypes.length && !filters.employmentTypes.includes(p.employmentType)) {
      return false;
    }
    if (term) {
      const hay = `${p.name} ${p.title ?? ""}`.toLowerCase();
      if (!hay.includes(term)) return false;
    }
    return true;
  };

  const filterTeam = (rt: RoleTeam): RoleTeam | null => {
    if (filters.roleTeamIds.length && !filters.roleTeamIds.includes(rt.id)) return null;
    const people = rt.people.filter(matchesPerson);
    if (people.length === 0) return null;
    return { ...rt, people };
  };

  const filterDept = (d: Department): Department | null => {
    if (filters.departmentIds.length && !filters.departmentIds.includes(d.id)) return null;
    const roleTeams = d.roleTeams.map(filterTeam).filter((rt): rt is RoleTeam => rt !== null);
    if (roleTeams.length === 0) return null;
    return { ...d, roleTeams };
  };

  return pillars
    .map((pillar) => {
      const departments = pillar.departments
        .map(filterDept)
        .filter((d): d is Department => d !== null);
      return { ...pillar, departments };
    })
    .filter((pillar) => pillar.departments.length > 0);
}

// ── Rows / cards ───────────────────────────────────────────────────────────

function PersonRow({ person }: { person: PersonLite }) {
  const accent = accentFor(person.employmentType);
  return (
    <OriginLink
      to="/dashboard/persons/$personId"
      params={{ personId: person.id }}
      aria-label={`Open ${person.name}`}
      className={cn(
        "flex items-center gap-2.5 border-l-2 py-1.5 pr-2 pl-2.5 transition-colors hover:bg-accent",
        accent.bar,
      )}
    >
      <PersonAvatar name={person.name} seed={person.id} size="sm" imageUrl={person.avatarUrl} />
      <div className="min-w-0 flex-1 leading-tight">
        <div className="truncate font-medium text-foreground text-sm">{person.name}</div>
        {person.title && (
          <div className="truncate text-muted-foreground text-xs">{person.title}</div>
        )}
      </div>
      <span className={cn("shrink-0 font-medium text-2xs", accent.chip)}>{accent.label}</span>
    </OriginLink>
  );
}

function RoleTeamCard({
  team,
  collapsed,
  onToggle,
}: {
  team: RoleTeam;
  collapsed: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="flex flex-col overflow-hidden rounded-lg border bg-card">
      <button
        type="button"
        aria-expanded={!collapsed}
        onClick={onToggle}
        className="flex items-center gap-2 border-b bg-muted/30 px-3 py-2 text-left transition-colors hover:bg-muted/50"
      >
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform",
            collapsed && "-rotate-90",
          )}
        />
        <span className="min-w-0 flex-1 truncate font-medium text-foreground text-sm">
          {team.name}
        </span>
        <span className="shrink-0 rounded-full bg-background px-2 py-0.5 text-2xs text-muted-foreground">
          {team.people.length}
        </span>
      </button>
      {!collapsed && (
        <div className="divide-y">
          {team.people.map((person) => (
            <PersonRow key={person.id} person={person} />
          ))}
        </div>
      )}
    </div>
  );
}

function DepartmentSection({
  department,
  collapsed,
  onToggle,
  collapsedTeams,
  toggleTeam,
  forceExpand,
}: {
  department: Department;
  collapsed: boolean;
  onToggle: () => void;
  collapsedTeams: Set<string>;
  toggleTeam: (id: string) => void;
  forceExpand: boolean;
}) {
  const headcount = department.roleTeams.reduce((sum, rt) => sum + rt.people.length, 0);
  return (
    <div className="space-y-2">
      <button
        type="button"
        aria-expanded={!collapsed}
        onClick={onToggle}
        className="flex w-full items-center gap-1.5 text-left"
      >
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform",
            collapsed && "-rotate-90",
          )}
        />
        <h3 className="font-medium text-muted-foreground text-sm uppercase tracking-wide">
          {department.name}
        </h3>
        <span className="text-muted-foreground text-xs">({headcount})</span>
      </button>
      {!collapsed && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {department.roleTeams.map((team) => (
            <RoleTeamCard
              key={team.id}
              team={team}
              collapsed={!forceExpand && collapsedTeams.has(team.id)}
              onToggle={() => toggleTeam(team.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PillarSection({
  pillar,
  collapsedDepts,
  toggleDept,
  collapsedTeams,
  toggleTeam,
  forceExpand,
}: {
  pillar: Pillar;
  collapsedDepts: Set<string>;
  toggleDept: (id: string) => void;
  collapsedTeams: Set<string>;
  toggleTeam: (id: string) => void;
  forceExpand: boolean;
}) {
  // Pillar accent bar — use the stored hex color when the admin set one, else a
  // neutral border so pre-color-picker pillars stay clean.
  const accentStyle = pillar.color?.startsWith("#") ? { backgroundColor: pillar.color } : undefined;

  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2">
        <span
          className={cn("h-4 w-1 rounded-full", accentStyle ? "" : "bg-border")}
          style={accentStyle}
        />
        <h2 className="font-semibold text-foreground text-lg">{pillar.name}</h2>
      </div>
      {pillar.departments.map((department) => (
        <DepartmentSection
          key={department.id}
          department={department}
          collapsed={!forceExpand && collapsedDepts.has(department.id)}
          onToggle={() => toggleDept(department.id)}
          collapsedTeams={collapsedTeams}
          toggleTeam={toggleTeam}
          forceExpand={forceExpand}
        />
      ))}
    </section>
  );
}

// ── Filter bar ───────────────────────────────────────────────────────────

function BoardFilterBar({
  filters,
  onChange,
}: {
  filters: BoardFilters;
  onChange: (next: BoardFilters) => void;
}) {
  // Resolve selected dept/team labels for the collapsed panel summary.
  const departmentLabels = useEntityOptionLabels("departments", filters.departmentIds);
  const roleTeamLabels = useEntityOptionLabels("role-teams", filters.roleTeamIds);
  // Employment-type chips come from the admin-managed list (EPIC-028) rather
  // than a literal that would silently omit a newly configured type.
  const employmentChips = useEmploymentTypeOptions().options;

  const activeCount =
    filters.employmentTypes.length + filters.departmentIds.length + filters.roleTeamIds.length;

  const summary: string[] = [];
  if (filters.employmentTypes.length)
    summary.push(`Employment Type: ${selectedLabels(employmentChips, filters.employmentTypes)}`);
  if (filters.departmentIds.length)
    summary.push(`Department: ${joinLabels(departmentLabels.data, filters.departmentIds)}`);
  if (filters.roleTeamIds.length)
    summary.push(`Role Team: ${joinLabels(roleTeamLabels.data, filters.roleTeamIds)}`);

  return (
    <div className="space-y-3">
      <FilterPanel
        activeCount={activeCount}
        summary={summary}
        onReset={() => onChange(EMPTY_FILTERS)}
        canReset={activeCount > 0 || filters.search.length > 0}
      >
        <FilterRow label="Employment Type">
          <FilterToggleGroup
            options={employmentChips}
            value={filters.employmentTypes}
            onChange={(v) => onChange({ ...filters, employmentTypes: v })}
          />
        </FilterRow>
        <FilterRow label="Department">
          <EntityComboboxMulti
            entity="departments"
            className="w-full sm:w-72"
            value={filters.departmentIds}
            placeholder="All departments"
            onChange={(departmentIds) => onChange({ ...filters, departmentIds })}
          />
        </FilterRow>
        <FilterRow label="Role Team">
          <EntityComboboxMulti
            entity="role-teams"
            className="w-full sm:w-72"
            value={filters.roleTeamIds}
            placeholder="All role teams"
            // Scope to the selected department(s) — teams under any of them.
            filters={
              filters.departmentIds.length
                ? { departmentId: filters.departmentIds.join(",") }
                : undefined
            }
            onChange={(roleTeamIds) => onChange({ ...filters, roleTeamIds })}
          />
        </FilterRow>
      </FilterPanel>

      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          value={filters.search}
          onValueChange={(search) => onChange({ ...filters, search })}
          placeholder="Search by name or title…"
          className="min-w-[200px] max-w-sm flex-1"
        />
      </div>
    </div>
  );
}

// Join resolved entity labels for a collapsed summary chip (count fallback
// while labels load).
function joinLabels(data: { label: string }[] | undefined, ids: string[]): string {
  return data?.length ? data.map((o) => o.label).join(", ") : `${ids.length} selected`;
}

// ── Board ──────────────────────────────────────────────────────────────────

export function OrgBoard() {
  const { data, isLoading, isError, error, refetch } = useOrganization();
  const [filters, setFilters] = useState<BoardFilters>(EMPTY_FILTERS);
  const debouncedSearch = useDebouncedValue(filters.search, 250);
  const [collapsedDepts, setCollapsedDepts] = useState<Set<string>>(new Set());
  const [collapsedTeams, setCollapsedTeams] = useState<Set<string>>(new Set());

  function toggleDept(id: string) {
    setCollapsedDepts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleTeam(id: string) {
    setCollapsedTeams((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  // While filtering, force every section open so matches are never hidden
  // behind a manually-collapsed department/team.
  const forceExpand =
    debouncedSearch.trim().length > 0 ||
    filters.employmentTypes.length > 0 ||
    filters.departmentIds.length > 0 ||
    filters.roleTeamIds.length > 0;

  const pillars = useMemo(
    () => filterPillars(data?.pillars ?? [], filters, debouncedSearch),
    [data?.pillars, filters, debouncedSearch],
  );

  if (isError) {
    return (
      <ErrorState message={error?.message ?? "Failed to load the organization"} onRetry={refetch} />
    );
  }

  const filterBar = <BoardFilterBar filters={filters} onChange={setFilters} />;

  if (isLoading) {
    return (
      <div className="space-y-4">
        {filterBar}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {SKELETON_KEYS.map((key) => (
            <div key={key} className="h-40 animate-pulse rounded-lg border bg-muted/40" />
          ))}
        </div>
      </div>
    );
  }

  const anyPlaced = (data?.pillars ?? []).some((p) =>
    p.departments.some((d) => d.roleTeams.some((rt) => rt.people.length > 0)),
  );

  return (
    <div className="space-y-4">
      {filterBar}
      {pillars.length === 0 ? (
        <EmptyState
          icon={Users}
          title={anyPlaced ? "No matches" : "No one placed yet"}
          description={
            anyPlaced
              ? "No one matches your current filters."
              : "Once people are assigned to a role team, they'll appear here grouped by pillar and department."
          }
          action={
            anyPlaced ? (
              <Button variant="outline" onClick={() => setFilters(EMPTY_FILTERS)}>
                Clear filters
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="space-y-8">
          {pillars.map((pillar) => (
            <PillarSection
              key={pillar.id}
              pillar={pillar}
              collapsedDepts={collapsedDepts}
              toggleDept={toggleDept}
              collapsedTeams={collapsedTeams}
              toggleTeam={toggleTeam}
              forceExpand={forceExpand}
            />
          ))}
        </div>
      )}
    </div>
  );
}
